import { z } from "zod";
import { Timestamps } from "../common/index.ts";
import {
  EntityId,
  MaterialId,
  MaterialRequestLineId,
  CustomerId,
  FsmTranslationsSchema,
} from "../fsm/fsm.schema.ts";
import { RequestId } from "../requests/requests.schema.ts";
import { BikeId } from "../bikes/bikes.schema.ts";
import { LocationId } from "../locations/locations.schema.ts";

export const MaterialCategory = z.enum([
  "Accesorios",
  "Consumible",
  "Dirección",
  "Frame Set",
  "Freno",
  "Iluminación",
  "Ruedas",
  "Sillín",
  "Sistema Eléctrico",
  "Transmisión",
]);
export type MaterialCategory = z.infer<typeof MaterialCategory>;

export const MATERIAL_CATEGORY_LABELS: Record<
  MaterialCategory,
  { en: string; es: string }
> = {
  Accesorios: { en: "Accessories", es: "Accesorios" },
  Consumible: { en: "Consumable", es: "Consumible" },
  Dirección: { en: "Steering", es: "Dirección" },
  "Frame Set": { en: "Frame Set", es: "Frame Set" },
  Freno: { en: "Brakes", es: "Freno" },
  Iluminación: { en: "Lighting", es: "Iluminación" },
  Ruedas: { en: "Wheels", es: "Ruedas" },
  Sillín: { en: "Saddle", es: "Sillín" },
  "Sistema Eléctrico": { en: "Electric System", es: "Sistema Eléctrico" },
  Transmisión: { en: "Drivetrain", es: "Transmisión" },
};

export const MaterialTranslations = z.object({
  name: FsmTranslationsSchema.optional(),
});
export type MaterialTranslations = z.infer<typeof MaterialTranslations>;

export function resolveMaterialLabel(
  material: { name: string; translations?: MaterialTranslations | null },
  locale: string,
): string {
  const lang = locale.startsWith("en") ? "en" : "es";
  return material.translations?.name?.[lang] ?? material.name;
}

export function resolveMaterialCategory(
  category: string,
  locale: string,
): string {
  const labels = MATERIAL_CATEGORY_LABELS[category as MaterialCategory];
  if (!labels) return category;
  return locale.startsWith("en") ? labels.en : labels.es;
}

export const MaterialDto = z
  .object({
    id: MaterialId,
    customerId: CustomerId,
    name: z.string(),
    category: z.string(),
    clientCost: z.number(),
    unit: z.string().nullable(),
    sku: z.string().nullable(),
    isConsumable: z.boolean(),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
    translations: MaterialTranslations.nullable().optional(),
    bikeModelIds: z.array(z.string().uuid()).optional(),
  })
  .merge(Timestamps);

export type MaterialDto = z.infer<typeof MaterialDto>;

// Minimal-fields DTO for request-builder pickers (gated by requests.write, not admin.materials.read)
export const MaterialPickerDto = z.object({
  id: MaterialId,
  name: z.string(),
  category: z.string(),
  clientCost: z.number(),
  unit: z.string().nullable(),
  isConsumable: z.boolean(),
  translations: MaterialTranslations.nullable().optional(),
  bikeModelIds: z.array(z.string().uuid()).optional(),
});
export type MaterialPickerDto = z.infer<typeof MaterialPickerDto>;
export const MaterialRequestLineDto = z
  .object({
    id: MaterialRequestLineId,
    requestId: RequestId,
    materialId: MaterialId,
    bikeId: BikeId.nullable(),
    quantity: z.number().int().positive(),
    shippedQuantity: z.number().int().nonnegative(),
    unitCost: z.number().nonnegative().nullable(),
    discountPercent: z.number().min(0).max(100).nullable(),
  })
  .merge(Timestamps);

export type MaterialRequestLineDto = z.infer<typeof MaterialRequestLineDto>;

/**
 * Effective (discounted) unit cost for a material request line — BEL-342.
 * `discountPercent` is a manual per-line override (not the catalog %, see BEL-414);
 * absent/null discount = catalog `unitCost` unchanged. Formula: docs/engineering-gotchas.md (BEL-821).
 */
export function computeEffectiveLineCost(line: {
  unitCost: number | null;
  discountPercent: number | null;
}): number | null {
  if (line.unitCost == null) return null;
  if (line.discountPercent == null) return line.unitCost;
  return line.unitCost * (1 - line.discountPercent / 100);
}

export const CreateMaterialInput = z.object({
  customerId: CustomerId.optional(), // BEL-730 — see createMaterial
  name: z.string().min(1),
  category: MaterialCategory,
  clientCost: z.number().nonnegative(),
  unit: z.string().optional(),
  sku: z.string().optional(),
  isConsumable: z.boolean().default(false),
  translations: MaterialTranslations.optional(),
});

export type CreateMaterialInput = z.infer<typeof CreateMaterialInput>;

export const UpdateMaterialInput = z.object({
  name: z.string().min(1).optional(),
  category: MaterialCategory.optional(),
  clientCost: z.number().nonnegative().optional(),
  unit: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  isConsumable: z.boolean().optional(),
  translations: MaterialTranslations.nullable().optional(),
});

export type UpdateMaterialInput = z.infer<typeof UpdateMaterialInput>;

export const CreateMaterialRequestLineInput = z.object({
  materialId: MaterialId,
  bikeId: BikeId.optional().nullable(),
  quantity: z.number().int().positive(),
  /**
   * Manual per-line discount, entered by an admin at creation time (BEL-415).
   * Same excepcional model as `AdminEditMaterialLineInput.discountPercent`
   * (BEL-342) — not a catalog %. Ignored server-side for callers without
   * `admin.materials.write`.
   */
  discountPercent: z.number().min(0).max(100).nullable().optional(),
});

export type CreateMaterialRequestLineInput = z.infer<
  typeof CreateMaterialRequestLineInput
>;

/** One material line edited via `admin_edit` (BEL-342). `id` present →
 * update (rejects lowering `quantity` below `shippedQuantity`); absent →
 * create a new line. */
export const AdminEditMaterialLineInput = z.object({
  id: MaterialRequestLineId.optional(),
  materialId: MaterialId,
  bikeId: BikeId.nullable().optional(),
  quantity: z.number().int().positive(),
  discountPercent: z.number().min(0).max(100).nullable().optional(),
  // BEL-654: admin-declared shipped amount, bound to `quantity` server-side.
  shippedQuantity: z.number().int().min(0).optional(),
});
export type AdminEditMaterialLineInput = z.infer<
  typeof AdminEditMaterialLineInput
>;

export const AdminEditMaterialLinesPayload = z.object({
  lines: z.array(AdminEditMaterialLineInput),
  removedLineIds: z.array(MaterialRequestLineId).optional(),
  requestDate: z.string().optional(),
  // BEL-487: optional override of the request's creator entity. Validated
  // server-side to belong to the same customer as the request.
  createdBy: EntityId.optional(),
  // BEL-488: optional override of the request's affected bike (scalar
  // `core_requests.bike_id`) for REP/WAR. Validated server-side to belong to
  // the same customer (and location, when set) as the request.
  bikeId: BikeId.optional(),
});
export type AdminEditMaterialLinesPayload = z.infer<
  typeof AdminEditMaterialLinesPayload
>;

export const MaterialRequestLineWithName = MaterialRequestLineDto.extend({
  materialName: z.string(),
  materialTranslations: MaterialTranslations.nullable().optional(),
});
export type MaterialRequestLineWithName = z.infer<
  typeof MaterialRequestLineWithName
>;

export const MaterialStockDto = z.object({
  locationId: LocationId,
  materialId: MaterialId,
  materialName: z.string(),
  unit: z.string().nullable(),
  quantityOnHand: z.number().int().nonnegative(),
  updatedAt: z.string(),
});
export type MaterialStockDto = z.infer<typeof MaterialStockDto>;

export const UpsertMaterialStockInput = z.object({
  quantityOnHand: z.number().int().nonnegative(),
});
export type UpsertMaterialStockInput = z.infer<typeof UpsertMaterialStockInput>;

export const BatchUpsertMaterialItem = z.object({
  sku: z.string(),
  name: z.string().min(1).optional(),
  nameEn: z.string().optional(),
  nameEs: z.string().optional(),
  category: MaterialCategory.optional(),
  unit: z.string().nullable().optional(),
  clientCost: z.number().min(0).optional(),
  isConsumable: z.boolean().optional(),
});
export type BatchUpsertMaterialItem = z.infer<typeof BatchUpsertMaterialItem>;

export const BatchUpsertMaterialInput = z.object({
  changes: z.object({
    upsert: z.array(BatchUpsertMaterialItem).min(1),
  }),
  source: z.string().optional(),
});
export type BatchUpsertMaterialInput = z.infer<typeof BatchUpsertMaterialInput>;

export const BatchMaterialItemResult = z.object({
  sku: z.string(),
  status: z.enum(["created", "updated", "unchanged", "error"]),
  changedFields: z.array(z.string()).optional(),
  error: z.string().optional(),
});
export type BatchMaterialItemResult = z.infer<typeof BatchMaterialItemResult>;

export const BatchMaterialResult = z.object({
  results: z.array(BatchMaterialItemResult),
  summary: z.object({
    created: z.number(),
    updated: z.number(),
    unchanged: z.number(),
    errors: z.number(),
  }),
  logId: z.string().uuid(),
});
export type BatchMaterialResult = z.infer<typeof BatchMaterialResult>;

export const BatchModelRelationItem = z.object({
  materialSku: z.string().min(1),
  bikeModelCode: z.string().min(1),
});
export type BatchModelRelationItem = z.infer<typeof BatchModelRelationItem>;

export const BatchModelRelationInput = z.object({
  relations: z.array(BatchModelRelationItem).min(1),
  strategy: z.enum(["replace", "merge"]),
  source: z.string().optional(),
});
export type BatchModelRelationInput = z.infer<typeof BatchModelRelationInput>;

export const BatchModelRelationError = z.object({
  materialSku: z.string(),
  bikeModelCode: z.string(),
  error: z.string(),
});
export type BatchModelRelationError = z.infer<typeof BatchModelRelationError>;

export const BatchModelRelationResult = z.object({
  added: z.number(),
  removed: z.number(),
  unchanged: z.number(),
  errors: z.array(BatchModelRelationError),
  logId: z.string().uuid(),
});
export type BatchModelRelationResult = z.infer<typeof BatchModelRelationResult>;
